import { requestGQL } from 'common/graphQlClient';
import { useQueryClient } from 'react-query';
import { useOfflineMutation } from 'common/offline/useOfflineMutation';
import { generateNodeId } from 'common/offline/generateNodeId';
import { NodeData } from 'app/connection/api';

import { CREATE_OUTLET, GetOutletsResponse } from '../api';

type CreateOutletPayload = {
  propertyId: number;
  premiseId: number;
  outletId: string;
};

type OptimisticOutletData = {
  outletId: string;
  propertyId: number;
  premiseId: number;
};

export const useCreateOutlet = (propertyId: null | number, premiseId: null | number) => {
  const queryClient = useQueryClient();

  // Optimistic update function to add outlet to cache immediately
  const applyOptimisticUpdate = (optimisticData: unknown) => {
    const data = optimisticData as OptimisticOutletData;
    const cacheKey = ['propertyConnection', propertyId];

    queryClient.setQueryData<GetOutletsResponse | undefined>(cacheKey, (oldData) => {
      if (!oldData?.propertyById?.connectionByConnectionId?.serialized2) {
        return oldData;
      }

      const newOutletNode: NodeData = {
        id: data.outletId,
        text: null,
        model: 'outlet',
        group: String(data.premiseId),
        cfObject: {
          images: [],
          location: undefined,
        },
      };

      return {
        ...oldData,
        propertyById: {
          ...oldData.propertyById,
          connectionByConnectionId: {
            ...oldData.propertyById.connectionByConnectionId,
            serialized2: {
              ...oldData.propertyById.connectionByConnectionId.serialized2,
              nodeDataArray: [
                ...oldData.propertyById.connectionByConnectionId.serialized2.nodeDataArray,
                newOutletNode,
              ],
            },
          },
        },
      };
    });
  };

  const mutation = useOfflineMutation<string, { message: string }, CreateOutletPayload>({
    mutationFn: async (payload) => requestGQL(CREATE_OUTLET, payload),
    mutationName: 'createOutlet',
    graphqlDocument: CREATE_OUTLET,
    entity: 'outlet',
    operationType: 'CREATE',
    propertyId: propertyId ?? undefined,
    invalidateKeys: [['propertyConnection', propertyId]],
    getOptimisticData: (payload) => ({
      ...payload,
    }),
    applyOptimisticUpdate,
  });

  return {
    ...mutation,
    mutate: () => {
      const outletId = generateNodeId();
      mutation.mutate({ propertyId: propertyId!, premiseId: premiseId!, outletId });
    },
  };
};
